import { FileReaderHelper } from '@/helpers/fileReaderHelper';
import { FundClassification } from '@/models/common/FundClassification';
import { FundGoals } from '@/models/common/FundGoals';
import type { DocumentDtoOut } from '@/models/out/DocumentDtoOut';
import type { OpenFundRequestDtoOut } from '@/models/out/OpenFundRequestDtoOut';
import type { NewCoordinatorDtoOut } from '@/models/out/NewCoordinatorDtoOut';
import type { NewSupportedDtoOut } from '@/models/out/NewSupportedDtoOut';
import type {
  CoordinatorFormData,
  OpenFundFormData,
  SupportedFormData,
} from '../schemas/openFundFormSchema';
import { PRIVACY_POLICY_VERSION } from '@/data/privacyPolicy';

const stripDataUrl = (dataUrl: string): string => {
  const commaIndex = dataUrl.indexOf(',');
  return commaIndex >= 0 ? dataUrl.substring(commaIndex + 1) : dataUrl;
};

const fileToDoc = async (file: File, description?: string): Promise<DocumentDtoOut> => {
  const docs = await FileReaderHelper.fileToDocumentDtoOut(file, description);
  return docs[0];
};

const mapAdditionalDocs = async (
  docs: { name: string; file: File }[]
): Promise<DocumentDtoOut[]> => {
  if (!docs || docs.length === 0) return [];
  return Promise.all(docs.map((d) => fileToDoc(d.file, d.name)));
};

const mapCoordinator = async (
  coordinator: CoordinatorFormData
): Promise<NewCoordinatorDtoOut> => ({
  firstName: coordinator.firstName,
  lastName: coordinator.lastName,
  israeliId: coordinator.israeliId,
  city: coordinator.city,
  street: coordinator.street,
  houseNumber: coordinator.houseNumber,
  email: coordinator.email,
  phone: coordinator.phone,
  idCardPhotocopy: await fileToDoc(coordinator.idCardPhotocopy!, 'coordinator-id-card'),
  relationToSupported: coordinator.relationToSupported || undefined,
  idAppendixPhotocopy: coordinator.idAppendixPhotocopy
    ? await fileToDoc(coordinator.idAppendixPhotocopy, 'coordinator-id-appendix')
    : undefined,
  additionalDocuments: await mapAdditionalDocs(coordinator.additionalDocuments as { name: string; file: File }[]),
  spouseFirstName: coordinator.spouseFirstName || undefined,
  spouseIsraeliId: coordinator.spouseIsraeliId || undefined,
});

const mapSupported = async (
  supported: SupportedFormData
): Promise<NewSupportedDtoOut> => ({
  firstName: supported.firstName,
  lastName: supported.lastName,
  israeliId: supported.israeliId,
  city: supported.city,
  street: supported.street,
  houseNumber: supported.houseNumber,
  email: supported.email,
  phone: supported.phone,
  idCardPhotocopy: await fileToDoc(supported.idCardPhotocopy!, 'supported-id-card'),
  idAppendixPhotocopy: await fileToDoc(
    supported.idAppendixPhotocopy!,
    'supported-id-appendix'
  ),
  additionalDocuments: await mapAdditionalDocs(supported.additionalDocuments as { name: string; file: File }[]),
  relationshipToFirstSupported: supported.relationshipToFirstSupported,
});

const signatureToDoc = (signatureDataUrl: string): DocumentDtoOut => ({
  name: 'signature.png',
  description: 'signature',
  contentBase64: stripDataUrl(signatureDataUrl),
});

const supportedToCoordinator = async (
  supported: SupportedFormData
): Promise<NewCoordinatorDtoOut> => ({
  firstName: supported.firstName,
  lastName: supported.lastName,
  israeliId: supported.israeliId,
  city: supported.city,
  street: supported.street,
  houseNumber: supported.houseNumber,
  email: supported.email,
  phone: supported.phone,
  idCardPhotocopy: await fileToDoc(supported.idCardPhotocopy!, 'coordinator-id-card'),
});

export const mapFormDataToDto = async (
  data: OpenFundFormData,
  /** When the applicant accepted the privacy policy on the intro screen, if they came through it. */
  privacyAcceptedAt?: string
): Promise<OpenFundRequestDtoOut> => {
  const isWelfare = data.fundClassification === FundClassification.Welfare;

  const supporteds: NewSupportedDtoOut[] = isWelfare
    ? await Promise.all(data.supporteds.map(mapSupported))
    : [];

  const coordinators: NewCoordinatorDtoOut[] =
    isWelfare && data.isCoordinator
      ? [
          ...(await Promise.all(data.supporteds.map(supportedToCoordinator))),
          ...(await Promise.all(data.coordinators.map(mapCoordinator))),
        ]
      : await Promise.all(data.coordinators.map(mapCoordinator));

  const fundGoals: FundGoals[] = isWelfare ? data.fundGoals : (data as any).fundGoals ?? [];
  const otherGoalDescription = data.fundClassification === FundClassification.Welfare
    ? data.otherGoalDescription
    : (data as any).otherGoalDescription || undefined;

  const isCommunity = data.fundClassification === FundClassification.Community;
  const responsibleParty = isCommunity ? (data as any).responsibleParty : undefined;
  const responsibleEntityName = isCommunity ? (data as any).responsibleEntityName : undefined;

  const supportedAwareOfFund = isWelfare ? (data as any).supportedAwareOfFund : undefined;
  const supportedCanViewFund = isWelfare ? (data as any).supportedCanViewFund : undefined;

  // The applicant-details section was removed from the form; the first coordinator serves as the
  // applicant/contact for notification emails.
  const primaryContact = coordinators[0];

  return {
    fundName: data.fundName,
    fundClassification: data.fundClassification,
    fundGoals,
    otherGoalDescription,
    reasonSummary: data.reasonSummary || undefined,
    responsibleParty,
    responsibleEntityName,
    supporteds,
    coordinators,
    applicantDetails: {
      firstName: primaryContact?.firstName ?? '',
      lastName: primaryContact?.lastName ?? '',
      email: primaryContact?.email ?? '',
    },
    personalInterfaceInNedarimPlus: data.personalInterfaceInNedarimPlus,
    crowdfundingCampaign: data.crowdfundingCampaign,
    termsAndConditionsAccepted: data.termsAndConditionsAccepted,
    regulationsAccepted: (data as any).regulationsAccepted ?? false,
    supportedAwareOfFund,
    supportedCanViewFund,
    privacyPolicyAccepted: !!privacyAcceptedAt,
    privacyPolicyVersion: privacyAcceptedAt ? PRIVACY_POLICY_VERSION : undefined,
    privacyPolicyAcceptedAt: privacyAcceptedAt,
    signature: signatureToDoc(data.signature),
  };
};
